import React from "react";
import { motion } from "framer-motion";
import TableRow from "./TableRow";

const DataTable = ({
  data = [],
  columns = [],
  onEdit,
  onDelete,
  emptyMessage = "No data available",
  currentUserId = null,
}) => {
  return (
    <div className="w-full">
      {/* Mobile Card View */}
      <div className="block md:hidden space-y-3">
        {data.length === 0 ? (
          <p className="text-center py-8 text-sm text-faint">
            {emptyMessage}
          </p>
        ) : (
          data.map((row, index) => {
            const isCurrentUser =
              currentUserId != null && String(row.id) === String(currentUserId);
            const showDelete = onDelete && !isCurrentUser;
            return (
              <motion.div
                key={row.id || index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-panel backdrop-blur-xl rounded-2xl border border-line shadow-card overflow-hidden"
              >
                <div className="p-4 divide-y divide-[var(--line)]">
                  {columns.map((col, ci) => (
                    <div
                      key={ci}
                      className="flex items-start justify-between gap-2 py-2 first:pt-0 last:pb-0"
                    >
                      <span className="text-xs font-medium text-faint flex-shrink-0">
                        {col.header}
                      </span>
                      <span className="text-sm text-ink text-right">
                        {col.render
                          ? col.render(row[col.key], row)
                          : row[col.key] || "—"}
                      </span>
                    </div>
                  ))}
                </div>
                {(onEdit || showDelete) && (
                  <div className="px-4 py-2.5 border-t border-line flex gap-2">
                    {onEdit && (
                      <motion.button
                        whileTap={{ scale: 0.95 }}
                        onClick={() => onEdit(row)}
                        className="flex-1 py-1.5 text-sm font-medium text-accent-ink bg-accent-soft rounded-xl flex items-center justify-center gap-1.5"
                      >
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          strokeWidth={2.5}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                        Edit
                      </motion.button>
                    )}
                    {showDelete && (
                      <motion.button
                        whileTap={{ scale: 0.95 }}
                        onClick={() => onDelete(row)}
                        className="flex-1 py-1.5 text-sm font-medium text-danger bg-danger-soft rounded-xl flex items-center justify-center gap-1.5"
                      >
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          strokeWidth={2.5}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                        Delete
                      </motion.button>
                    )}
                  </div>
                )}
              </motion.div>
            );
          })
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block w-full overflow-x-auto scrollbar-thin">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="inline-block min-w-full align-middle"
        >
          <div className="overflow-hidden rounded-card border border-line bg-panel backdrop-blur-xl shadow-card">
            <table className="min-w-full">
              {/* Table Header */}
              <thead>
                <tr>
                  {columns.map((column, index) => (
                    <th
                      key={index}
                      className="px-5 py-3.5 text-left text-xs font-medium text-faint whitespace-nowrap border-b border-line"
                    >
                      {column.header}
                    </th>
                  ))}
                  {(onEdit || onDelete) && (
                    <th className="px-5 py-3.5 text-left text-xs font-medium text-faint whitespace-nowrap border-b border-line">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>

              {/* Table Body */}
              <tbody>
                {data.length === 0 ? (
                  <tr>
                    <td
                      colSpan={columns.length + (onEdit || onDelete ? 1 : 0)}
                      className="px-4 py-8 text-center text-faint"
                    >
                      {emptyMessage}
                    </td>
                  </tr>
                ) : (
                  data.map((row, index) => (
                    <TableRow
                      key={row.id || index}
                      data={row}
                      columns={columns}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      index={index}
                      currentUserId={currentUserId}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default DataTable;
